import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "../components/LocaleLink";
import { ArrowLeft, ChevronRight, ArrowRight } from "lucide-react";
import { getBlogPost } from "../data/blog";
import { useLang } from "../context/LanguageContext";
import { optimizeImg, fetchBlogPostBySlug, localized, localizedList, resolveMediaUrl } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ShareButtons } from "../components/ShareButtons";
import { CiteThisArticle } from "../components/CiteThisArticle";
import { FAQSection } from "../components/FAQSection";
import { JsonLd, buildArticleSchema, buildFaqSchema, buildBreadcrumbSchema } from "../components/JsonLd";
import { RichParagraph, stripMarkdown } from "../components/RichText";
import { OwnerEstimateBanner } from "../components/OwnerEstimateBanner";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "https://velmiraliving.com");

export default function BlogPost() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [post, setPost] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    const normalize = (p) => ({
      slug: p.slug || slug,
      title: typeof p.title === "string" ? p.title : localized(p.title, lang),
      description: typeof p.description === "string" ? p.description : localized(p.description, lang),
      image: p.image,
      date: p.date,
      author: p.author,
      body: Array.isArray(p.body) ? p.body : localizedList(p.body, lang),
      faq: Array.isArray(p.faq) ? p.faq : localizedList(p.faq, lang),
      seo: p.seo && typeof p.seo === "object" ? p.seo[lang] || p.seo.en || {} : {},
      internal_links: p.internal_links || [],
    });

    setLoaded(false);
    fetchBlogPostBySlug(slug)
      .then((p) => { if (active) { setPost(normalize(p)); setLoaded(true); } })
      .catch(() => {
        const s = getBlogPost(slug, lang);
        if (active) { setPost(s ? normalize(s) : null); setLoaded(true); }
      });
    return () => { active = false; };
  }, [slug, lang]);

  if (!loaded) {
    return <div className="pt-44 pb-32 text-center text-foreground/60 font-body" data-testid="blogpost-loading">Loading…</div>;
  }

  if (!post) {
    return (
      <div className="pt-44 pb-32 text-center" data-testid="blogpost-notfound">
        <Seo title="Article not found | Velmira Living" description="This article does not exist." noindex />
        <p className="font-headings text-3xl text-foreground">{t("blogUi.notFound")}</p>
        <Link to="/blog" className="mt-6 inline-block text-primary uppercase tracking-widest text-xs">{t("blogUi.backToBlog")}</Link>
      </div>
    );
  }

  const enTitle = `${post.title?.en || ""} ${post.slug || ""}`;
  const ownerTopic = /airbnb|rental|rent |rent-|management|quota|yield|owner|changement|conciergerie|tourist/i.test(enTitle) && !/buy|buyer|price-per|cost-of-living|visa|residency|inheritance|notaire|renovation|monaco|retir/i.test(enTitle);
  const base = `${origin()}/${lang}`;
  const url = `${base}/blog/${slug}`;
  const breadcrumb = [
    { name: t("blogUi.home"), url: base },
    { name: t("blogUi.blog"), url: `${base}/blog` },
    { name: post.title, url },
  ];
  const seoTitle = post.seo?.seo_title || `${post.title} | Velmira Living`;
  const seoDesc = post.seo?.meta_description || post.description;
  const heroImg = post.image ? resolveMediaUrl(optimizeImg(post.image, 1200)) : "";

  return (
    <article data-testid="blog-post-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo title={seoTitle} description={seoDesc} image={heroImg || undefined} />
      <JsonLd id="article" data={buildArticleSchema({ ...post, image: heroImg || post.image }, url)} />
      <JsonLd id="article-breadcrumb" data={buildBreadcrumbSchema(breadcrumb)} />
      {post.faq?.length ? <JsonLd id="article-faq" data={buildFaqSchema(post.faq.map((f) => ({ ...f, a: stripMarkdown(f.a) })))} /> : null}

      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[820px]">
        <nav aria-label="Breadcrumb" data-testid="blog-breadcrumb" className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-8">
          <Link to="/" className="hover:text-primary">{t("blogUi.home")}</Link>
          <ChevronRight size={12} />
          <Link to="/blog" className="hover:text-primary">{t("blogUi.blog")}</Link>
        </nav>

        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-primary transition-colors mb-6">
          <ArrowLeft size={15} /> {t("blogUi.backToBlog")}
        </Link>

        <Reveal>
          <span className="font-accent text-[11px] uppercase tracking-[0.2em] text-primary">{new Date(post.date).toLocaleDateString(t("blogUi.locale"), { year: "numeric", month: "long", day: "numeric" })}</span>
          <h1 className="mt-4 font-headings text-4xl sm:text-5xl leading-[1.1] tracking-tight text-foreground">{post.title}</h1>
          <p className="mt-4 font-body text-sm text-foreground/60" data-testid="blog-byline">
            Velmira Living Editorial Team · {t("blogUi.reviewedBy")}{" "}
            <Link to="/author/gorkem" data-testid="blog-author-link" className="text-primary hover:underline">
              Görkem — Founder & Head of Operations
            </Link>
          </p>
          <div className="mt-6"><ShareButtons url={url} title={post.title} /></div>
        </Reveal>

        {heroImg ? (
          <Reveal delay={0.1}>
            <div className="mt-10 aspect-[16/9] overflow-hidden">
              <img src={heroImg} alt={post.title} decoding="async" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        ) : null}

        <div className="mt-12 space-y-10">
          {post.body.map((block, i) => (
            <Reveal key={i}>
              <section>
                {block.h2 ? <h2 className="font-headings text-2xl sm:text-3xl text-foreground">{block.h2}</h2> : null}
                {block.p.map((para, j) => (
                  <RichParagraph key={j} text={para} />
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        {post.internal_links?.length ? (
          <Reveal>
            <div data-testid="blog-internal-links" className="mt-16 border-t border-border/60 pt-8">
              <p className="font-accent text-[11px] uppercase tracking-[0.2em] text-primary mb-4">{t("blogUi.exploreMore") || "Explore more"}</p>
              <div className="flex flex-wrap gap-2">
                {post.internal_links.map((l, i) => (
                  <Link key={i} to={`/${l.slug}`} className="text-sm font-body border border-border/60 hover:border-primary hover:text-primary px-4 py-2 transition-all">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        ) : null}

        <FAQSection faqs={post.faq} title={t("blogUi.faqTitle")} />

        <Reveal>
          <CiteThisArticle
            title={post.title}
            author={post.author || "Velmira Living"}
            date={post.date}
            url={url}
          />
        </Reveal>

        {ownerTopic ? (
          <OwnerEstimateBanner location="blog_article" airbnb={/airbnb/i.test(enTitle)} className="!px-0 mt-16" />
        ) : (
        <div data-testid="blog-cta" className="mt-16 bg-secondary border border-border/60 p-10 text-center">
          <h2 className="font-headings text-2xl sm:text-3xl text-foreground">{t("blogUi.ctaTitle")}</h2>
          <Link to="/contact?interest=general" className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
            {t("blogUi.ctaButton")} <ArrowRight size={15} />
          </Link>
        </div>
        )}
      </div>
    </article>
  );
}
